/**
 * src/pages/reports/reportCsv.js — "Download figures" (card P224 item 2).
 *
 * ⛔⛔ IT EXPORTS THE PAYLOAD AND NOTHING ELSE. The figures panel already
 * shows this object; the CSV is the same thing in a shape a spreadsheet can
 * open. In particular it does NOT export the model's prose: the brief is
 * words about the figures, and a column of sentences in a finance workbook is
 * neither checkable nor aggregate.
 *
 * ⚠⚠ THE PAYLOAD IS AGGREGATES-ONLY AND THAT IS ENFORCED ELSEWHERE — the CEO
 * payload-shape guard refuses a forbidden key at the source. This file does
 * not re-police it, because a second allowlist would be a second answer that
 * drifts. What it DOES do is flatten only what it can name, so a nested
 * structure nobody anticipated appears as a JSON scalar in one cell rather
 * than being silently dropped or silently spread.
 */

/** RFC-4180 escaping. ⚠ A stage name containing a comma is ordinary here. */
export function csvCell(v) {
  if (v === null || v === undefined) return '';
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Flatten the payload into (block, key, field, value) rows.
 *
 * ⛔ FOUR COLUMNS, NOT A WIDE TABLE. The payload is several unrelated blocks —
 * per-rep rows, per-stage rows, a receivables summary, scalar metrics — and a
 * single wide sheet would invent a relationship between them. Long form is
 * honest and pivots cleanly.
 */
export function payloadToCsvRows(payload) {
  const rows = [];
  if (!payload || typeof payload !== 'object') return rows;

  const pushScalar = (block, key, field, value) => {
    rows.push([block, key, field, value]);
  };

  const walk = (block, node, key = '') => {
    if (node === null || node === undefined) return;
    if (Array.isArray(node)) {
      node.forEach((row, i) => {
        if (row && typeof row === 'object' && !Array.isArray(row)) {
          for (const [f, v] of Object.entries(row)) pushScalar(block, key || String(i + 1), f, v);
        } else {
          pushScalar(block, key || String(i + 1), '', row);
        }
      });
      return;
    }
    if (typeof node === 'object') {
      /* ⚠ A probe block: unwrap { tracked, data } so the sheet carries the
         DATA, and record `tracked:false` explicitly rather than emitting an
         empty block that reads as "nothing happened". */
      if (Object.prototype.hasOwnProperty.call(node, 'tracked')) {
        if (!node.tracked) { pushScalar(block, key || '(block)', 'tracked', false); return; }
        walk(block, node.data, key);
        return;
      }
      for (const [k, v] of Object.entries(node)) {
        if (v && typeof v === 'object') walk(block, v, k);
        else pushScalar(block, key || k, key ? k : '', v);
      }
      return;
    }
    pushScalar(block, key, '', node);
  };

  for (const [block, node] of Object.entries(payload)) {
    if (node === null || typeof node !== 'object') {
      pushScalar(block, '', '', node);
      continue;
    }
    walk(block, node);
  }
  return rows;
}

/** The whole file, header included. */
export function payloadToCsv(payload) {
  const rows = payloadToCsvRows(payload);
  const head = ['block', 'key', 'field', 'value'];
  return [head, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
}

/** Mirrors pdfFileName()'s rules so a pair of downloads sorts together. */
export function csvFileName(pipelineLabel, generatedAt) {
  const d = generatedAt ? new Date(generatedAt) : new Date();
  const ymd = Number.isNaN(d.getTime())
    ? new Date().toISOString().slice(0, 10)
    : [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'),
       String(d.getDate()).padStart(2, '0')].join('-');
  const safe = String(pipelineLabel || 'All pipelines')
    .replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim().slice(0, 60);
  return `ZignaGenix AI pipeline figures - ${safe} - ${ymd}.csv`;
}
